import {
  BIOME_SETS,
  type BiomeId,
  ITEMS,
  type PieceRarities,
  RARITY_COLORS,
  type Rarity,
  type VisibleSlot,
  VISIBLE_SLOTS,
} from '@shefira/shared';
import type Phaser from 'phaser';

import { BLAZE_RAMP, blazeShade, hash } from './blaze.js';
import { opaquePixels } from './upgrade-shine.js';

/**
 * What a piece's rarity looks like on the body (ADR 0061). The upgrade owns
 * the whole outfit (halo, stars, the name's colour); rarity stays inside each
 * piece, so the two never read as one another even where their colours meet:
 *
 * - rare: now and then a light sheen sweeps across the piece;
 * - epic: a quicker sheen, and a one-pixel outline in the rarity's colour
 *   that lights up now and then, where the piece meets empty space;
 * - legendary: a burning outline, always on, light running round it and
 *   flames licking up from it, and motes of its biome
 *   (petals, leaves, spores, embers, flakes) rising off the piece.
 *
 * A common piece does nothing. The rarity is the real piece's, sent by the
 * server (`PlayerState.rarities`), so a cosmetic over a legendary shines like
 * it; its motes are the biome's only when the drawing shown is the set's.
 * Everything is one-pixel dots on whole pixels, redrawn on a single Graphics
 * each tick.
 */

const TICK_MS = 90;

interface Sheen {
  /** Ticks between two sweeps, and pixels a sweep moves each tick. */
  every: number;
  speed: number;
}

const SHEEN: Record<Rarity, Sheen | null> = {
  common: null,
  rare: { every: 50, speed: 2 },
  epic: { every: 30, speed: 2 },
  legendary: { every: 22, speed: 3 },
};

/** Epic: the outline shows this many ticks out of every `EPIC_OUTLINE_EVERY`. */
const EPIC_OUTLINE_EVERY = 34;
const EPIC_OUTLINE_ON = 10;
/**
 * Legendary: the outline never goes out, it burns. Light runs round the piece
 * along `BLAZE_RAMP` (dark, the colour, gold, white-hot), a lap every
 * `BLAZE_LAP` ticks, and short flames lick up from its top edges. Bright arcs
 * chase each other round the piece.
 */
const BLAZE_ARCS = 3;
const BLAZE_LAP = 18;
/** How often a top-edge pixel throws a flame, and its tallest tongue. */
const FLAME_CHANCE = 0.3;
const FLAME_HEIGHT = 3;

interface MoteStyle {
  colors: readonly number[];
  /** Ticks a mote lives, pixels it rises per tick, and whether it sways. */
  life: number;
  rise: number;
  sway: boolean;
}

/** Each biome's legendary sheds what its land is made of. */
const MOTES: Record<BiomeId | 'none', MoteStyle> = {
  meadow: { colors: [0xfff27a, 0xffffff, 0xff9ecf], life: 9, rise: 1, sway: true },
  forest: { colors: [0x7bd34f, 0x3e9e3a, 0xc9e86a], life: 10, rise: 1, sway: true },
  swamp: { colors: [0x9be07a, 0x5fc2a0, 0xd6ff9c], life: 8, rise: 1, sway: false },
  desert: { colors: [0xffc23a, 0xff7a1c, 0xfff0a0], life: 7, rise: 2, sway: false },
  snow: { colors: [0xffffff, 0xc8ecff, 0x8fd4ff], life: 11, rise: 1, sway: true },
  none: { colors: [RARITY_COLORS.legendary, 0xffffff], life: 8, rise: 1, sway: false },
};
/** A legendary piece lets a mote go every this many ticks, on average. */
const MOTE_EVERY = 3;

interface Worn {
  rarity: Rarity;
  motes: MoteStyle;
}

interface Mote {
  x: number;
  y: number;
  age: number;
  color: number;
  style: MoteStyle;
  sway: number;
}

/** The biome a piece's drawing belongs to, from the sets' art ids. */
const BIOME_OF_ART: ReadonlyMap<string, BiomeId> = new Map(
  BIOME_SETS.flatMap((set) =>
    Object.values(set.pieces).map((piece) => [`overlay.${piece.id}`, set.biome] as const),
  ),
);

/** The motes of the drawing shown on a slot: its set's biome, or plain sparks. */
function motesOf(templateId: string | undefined): MoteStyle {
  const sprite = templateId === undefined ? undefined : ITEMS.get(templateId)?.sprite;
  return MOTES[(sprite === undefined ? undefined : BIOME_OF_ART.get(sprite)) ?? 'none'];
}

export interface RarityShineOptions {
  scene: Phaser.Scene;
  /** The sprite drawn for a slot, as the upgrade stars find it. */
  pieceOf: (slot: VisibleSlot) => Phaser.GameObjects.Sprite | undefined;
  /**
   * The layers drawn over a slot's piece: the outline stays off them, and
   * goes over whatever is behind the piece (the body behind a shield).
   */
  layersAbove: (slot: VisibleSlot) => Iterable<Phaser.GameObjects.Sprite>;
  /** Every layer: a legendary's flames rise only into the air around the character. */
  layers: () => Iterable<Phaser.GameObjects.Sprite>;
  /** The mount ridden: nothing of the shine is ever drawn on it. */
  mount: () => Phaser.GameObjects.Sprite | undefined;
}

type Pixel = readonly [number, number];

export class RarityShine {
  /** In front of the character's layers, under the upgrade stars. */
  readonly front: Phaser.GameObjects.Container;
  private readonly graphics: Phaser.GameObjects.Graphics;
  /** Pixels of the mount this tick, where no dot lands; null on foot. */
  private mask: Set<number> | null = null;
  private worn: Partial<Record<VisibleSlot, Worn>> = {};
  /** Each sweeping piece: its tick counter and, mid-sweep, the diagonal it has reached. */
  private sweeps: Partial<Record<VisibleSlot, { wait: number; at: number | null }>> = {};
  private motes: Mote[] = [];
  private ticks = 0;
  private readonly timer: Phaser.Time.TimerEvent;

  constructor(private readonly options: RarityShineOptions) {
    const { scene } = options;
    this.front = scene.add.container(0, 0);
    this.graphics = scene.add.graphics();
    this.front.add(this.graphics);
    this.timer = scene.time.addEvent({
      delay: TICK_MS,
      loop: true,
      callback: () => {
        this.tick();
      },
    });
  }

  /** The real pieces' rarities, and the template drawn on each slot for its motes. */
  set(rarities: PieceRarities, appearance: Partial<Record<VisibleSlot, string>>): void {
    this.worn = {};
    for (const slot of VISIBLE_SLOTS) {
      const rarity = slot === 'wings' ? undefined : rarities[slot];
      if (rarity === undefined || rarity === 'common' || appearance[slot] === undefined) continue;
      this.worn[slot] = { rarity, motes: motesOf(appearance[slot]) };
      // Pieces don't all sweep together.
      this.sweeps[slot] ??= { wait: Math.floor(Math.random() * 20), at: null };
    }
  }

  get empty(): boolean {
    return Object.keys(this.worn).length === 0;
  }

  setVisible(visible: boolean): void {
    this.front.setVisible(visible);
  }

  destroy(): void {
    this.timer.remove(false);
    this.front.destroy(true);
  }

  private tick(): void {
    this.ticks += 1;
    const mount = this.options.mount();
    this.mask = mount?.visible === true ? this.occupied([mount]) : null;
    this.graphics.clear();
    if (!this.front.visible) return;
    let body: Set<number> | undefined;
    for (const slot of VISIBLE_SLOTS) {
      const worn = this.worn[slot];
      const piece = this.options.pieceOf(slot);
      if (worn === undefined || piece?.visible !== true) continue;
      const pixels = this.placed(piece);
      if (pixels.length === 0) continue;
      const outline = this.outlineAlpha(worn.rarity);
      if (outline > 0) {
        const covered = new Set(pixels.map(([x, y]) => key(x, y)));
        for (const cell of this.occupied(this.options.layersAbove(slot))) covered.add(cell);
        const ring = outlineOf(pixels, covered);
        if (worn.rarity === 'legendary') {
          body ??= this.occupied(this.options.layers());
          this.drawBlaze(ring, body);
        } else this.drawOutline(ring, RARITY_COLORS[worn.rarity], outline);
      }
      this.stepSheen(slot, worn.rarity, pixels);
      if (worn.rarity === 'legendary' && Math.random() < 1 / MOTE_EVERY) {
        this.release(pixels, worn.motes);
      }
    }
    this.stepMotes();
  }

  /** The piece's opaque pixels, where the container draws them right now. */
  private placed(sprite: Phaser.GameObjects.Sprite): Pixel[] {
    const left = Math.round(sprite.x - sprite.displayOriginX);
    const top = Math.round(sprite.y - sprite.displayOriginY);
    return opaquePixels(sprite.frame).map(([x, y]) => [left + x, top + y] as const);
  }

  /** Every pixel the layers over a piece cover, keyed by `key` (offsets stay small). */
  private occupied(layers: Iterable<Phaser.GameObjects.Sprite>): Set<number> {
    const cells = new Set<number>();
    for (const sprite of layers) {
      if (!sprite.visible) continue;
      for (const [x, y] of this.placed(sprite)) cells.add(key(x, y));
    }
    return cells;
  }

  private outlineAlpha(rarity: Rarity): number {
    if (rarity === 'epic') {
      const phase = this.ticks % EPIC_OUTLINE_EVERY;
      if (phase >= EPIC_OUTLINE_ON) return 0;
      // Fade in and out over the few ticks it shows.
      return 0.9 * Math.sin((Math.PI * (phase + 1)) / (EPIC_OUTLINE_ON + 1));
    }
    return rarity === 'legendary' ? 1 : 0;
  }

  /** One pixel of the shine, unless the mount is there. */
  private dot(x: number, y: number): void {
    if (this.mask?.has(key(x, y)) === true) return;
    this.graphics.fillRect(x, y, 1, 1);
  }

  private drawOutline(ring: readonly Pixel[], color: number, alpha: number): void {
    this.graphics.fillStyle(color, alpha);
    for (const [x, y] of ring) this.dot(x, y);
  }

  /**
   * The legendary outline: every pixel lit, its shade from where it sits
   * round the piece's centre and the tick, so bright arcs run round it; and
   * from each pixel with open air above, now and then a flame one to three
   * pixels tall, hottest at its root.
   */
  private drawBlaze(ring: readonly Pixel[], body: ReadonlySet<number>): void {
    if (ring.length === 0) return;
    let cx = 0;
    let cy = 0;
    for (const [x, y] of ring) {
      cx += x;
      cy += y;
    }
    cx /= ring.length;
    cy /= ring.length;
    const turn = (2 * Math.PI * this.ticks) / BLAZE_LAP;
    const top = BLAZE_RAMP.length - 1;
    for (const [x, y] of ring) {
      const wave = (1 + Math.cos(BLAZE_ARCS * Math.atan2(y - cy, x - cx) - turn)) / 2;
      this.graphics.fillStyle(blazeShade(wave), 1);
      this.dot(x, y);
      if (body.has(key(x, y - 1))) continue;
      // Flicker from a hash of the column and the tick: steady for a tick, never in step.
      const roll = hash(x, y, this.ticks);
      if (roll >= FLAME_CHANCE) continue;
      const height = 1 + Math.floor((roll / FLAME_CHANCE) * FLAME_HEIGHT);
      for (let step = 1; step <= height; step += 1) {
        if (body.has(key(x, y - step))) break;
        const heat = BLAZE_RAMP[Math.max(0, top - step)] ?? BLAZE_RAMP[0];
        this.graphics.fillStyle(heat, 1 - (step - 1) / (height + 1));
        this.dot(x, y - step);
      }
    }
  }

  /**
   * A two-pixel band along the anti-diagonal, crossing the piece top-left to
   * bottom-right, lighting only the piece's own pixels.
   */
  private stepSheen(slot: VisibleSlot, rarity: Rarity, pixels: readonly Pixel[]): void {
    const sheen = SHEEN[rarity];
    const sweep = this.sweeps[slot];
    if (sheen === null || sweep === undefined) return;
    let low = Infinity;
    let high = -Infinity;
    for (const [x, y] of pixels) {
      low = Math.min(low, x + y);
      high = Math.max(high, x + y);
    }
    if (sweep.at === null) {
      sweep.wait -= 1;
      if (sweep.wait > 0) return;
      sweep.at = low - 1;
    }
    sweep.at += sheen.speed;
    if (sweep.at > high + 1) {
      sweep.at = null;
      sweep.wait = sheen.every + Math.floor(Math.random() * 8);
      return;
    }
    for (const [x, y] of pixels) {
      const d = x + y - sweep.at;
      if (d === 0) this.graphics.fillStyle(0xffffff, 0.75);
      else if (d === -1 || d === 1) this.graphics.fillStyle(0xffffff, 0.35);
      else continue;
      this.dot(x, y);
    }
  }

  private release(pixels: readonly Pixel[], style: MoteStyle): void {
    const at = pixels[Math.floor(Math.random() * pixels.length)];
    if (at === undefined) return;
    this.motes.push({
      x: at[0],
      y: at[1],
      age: 0,
      color: style.colors[Math.floor(Math.random() * style.colors.length)] ?? 0xffffff,
      style,
      sway: Math.random() < 0.5 ? -1 : 1,
    });
  }

  private stepMotes(): void {
    this.motes = this.motes.filter((mote) => {
      mote.age += 1;
      if (mote.age > mote.style.life) return false;
      mote.y -= mote.style.rise;
      if (mote.style.sway && mote.age % 3 === 0) mote.sway = -mote.sway;
      if (mote.style.sway && mote.age % 2 === 0) mote.x += mote.sway;
      const alpha = mote.age < mote.style.life / 2 ? 1 : 2 * (1 - mote.age / mote.style.life);
      this.graphics.fillStyle(mote.color, Math.max(0.15, alpha));
      this.dot(mote.x, mote.y);
      return true;
    });
  }
}

const NEIGHBOURS: readonly Pixel[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

/** Empty pixels next to the piece, once each: neither it nor a layer over it fills them. */
function outlineOf(pixels: readonly Pixel[], covered: ReadonlySet<number>): Pixel[] {
  const done = new Set<number>();
  const ring: Pixel[] = [];
  for (const [x, y] of pixels) {
    for (const [dx, dy] of NEIGHBOURS) {
      const cell = key(x + dx, y + dy);
      if (covered.has(cell) || done.has(cell)) continue;
      done.add(cell);
      ring.push([x + dx, y + dy]);
    }
  }
  return ring;
}

function key(x: number, y: number): number {
  return (x + 2048) * 4096 + (y + 2048);
}
